import { Search } from 'lucide-react';
import type { ReactNode } from 'react';

/** Large title, one sentence of context, and at most one action. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">{title}</h1>
        {description && <p className="mt-1.5 text-[15px] text-ink2">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** The handoff's search field: a sunken fill with no edge until it is focused. */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  className?: string;
}) {
  return (
    <div className={`flex h-11 items-center gap-2.5 rounded-[10px] bg-search px-3.5 text-ink3 focus-within:shadow-[inset_0_0_0_1.5px_var(--accent),0_0_0_3px_var(--accent-ring)] ${className}`}>
      <Search size={18} strokeWidth={1.8} aria-hidden />
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink3"
      />
    </div>
  );
}

/** A card with a title and an explanatory line — used for the side forms. */
export function FormPanel({
  title,
  description,
  children,
  className = '',
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`overflow-hidden rounded-[14px] border border-hair bg-elev ${className}`}>
      <div className="px-5 pt-4">
        <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
        {description && <p className="mt-1 text-[13.5px] text-ink3">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/** Rows grouped under an eyebrow with a count — "Active 12", "Archived 3". */
export function RecordGroup({
  title,
  count,
  emptyLabel,
  children,
}: {
  title: string;
  count: number;
  emptyLabel: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2.5 ml-1 mt-6 flex items-center gap-2">
        <h2 className="eyebrow">{title}</h2>
        <span className="font-mono text-[12px] text-ink3">{count}</span>
      </div>
      {count === 0
        ? <p className="rounded-[14px] border border-hair bg-elev px-4 py-6 text-center text-[14.5px] text-ink2">{emptyLabel}</p>
        : <div className="flex flex-col gap-2">{children}</div>}
    </section>
  );
}
